import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import ReactPlayer from 'react-player';
import styled from 'styled-components';

import BubbleContent from 'components/customer/conversation_history/conversation_items_v2/content/bubble_content';
import ConversationMessageVideoPlayer from 'components/customer/conversation_history/conversation_items_v2/message/conversation_message_video_player';
import ConversationItem from 'models/conversation_item';
import { HandleHeightChange } from 'components/customer/conversation_history/virtualized/list_item';
import InformationIcon from 'components/lib/icons/information_icon';
import InlineAnnotatedContentContainer from 'components/lib/inline_annotated_content_container';
import ItemContext from 'components/customer/conversation_history/conversation_items_v2/item_context';
import { ModerationStatus } from 'scripts/domain/models/conversation_message';
import useConditionalTranslation from 'components/hooks/use_conditional_translation';
import VideoPlayIcon from 'components/lib/icons/video_play_icon';

export function InstagramCommentWithImage({ item, mediaUrl, isStacked }) {
  const [isLoadErr, setLoadErr] = useState(false);

  const onError = () => setLoadErr(true);

  if (isLoadErr) {
    return renderFallbackComment(item, isStacked);
  }

  if (isStacked) {
    return renderCommentBubble(item);
  }

  return (
    <HandleHeightChange>
      {({ onHeightChange }) => (
        <div>
          <InstagramCommentContainer>
            <PostContentContainer>
              <PostImageContainer>
                <PostImage alt="Instagram post preview" onError={onError} onLoad={onHeightChange} src={mediaUrl} />
              </PostImageContainer>
              <PostText>{item.content.getMediaText()}</PostText>
            </PostContentContainer>
          </InstagramCommentContainer>
          {renderCommentBubble(item)}
        </div>
      )}
    </HandleHeightChange>
  );
}

InstagramCommentWithImage.propTypes = {
  isStacked: PropTypes.bool,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  mediaUrl: PropTypes.string.isRequired,
};

export function InstagramCommentWithVideo({ item, mediaUrl, isStacked }) {
  const [isLoadErr, setLoadErr] = useState(false);

  const onError = () => setLoadErr(true);

  if (isLoadErr) {
    return renderFallbackComment(item, isStacked);
  }

  if (isStacked) {
    return renderCommentBubble(item);
  }

  const playButtonComponent = (
    <PlayButtonOverlay>
      <VideoPlayIcon />
    </PlayButtonOverlay>
  );

  return (
    <HandleHeightChange>
      {({ onHeightChange }) => (
        <div>
          <InstagramCommentContainer>
            <PostContentContainer>
              <PostVideoContainer>
                <ConversationMessageVideoPlayer
                  onError={onError}
                  onHeightChange={onHeightChange}
                  playButtonComponent={playButtonComponent}
                  url={mediaUrl}
                  videoPlayerComponent={VideoPlayer}
                  videoPlayerProps={{
                    style: { maxHeight: '200px', borderRadius: '8px' },
                  }}
                />
              </PostVideoContainer>
              <PostText>{item.content.getMediaText()}</PostText>
            </PostContentContainer>
          </InstagramCommentContainer>
          {renderCommentBubble(item)}
        </div>
      )}
    </HandleHeightChange>
  );
}

InstagramCommentWithVideo.propTypes = {
  isStacked: PropTypes.bool,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  mediaUrl: PropTypes.string.isRequired,
};

function renderFallbackComment(item, isStacked) {
  if (isStacked) {
    return renderCommentBubble(item);
  }

  return (
    <div>
      <InstagramCommentContainer>
        <PostContentContainer>
          <PostText>{item.content.getMediaText()}</PostText>
        </PostContentContainer>
      </InstagramCommentContainer>
      {renderCommentBubble(item)}
    </div>
  );
}

function renderCommentBubble(item) {
  return (
    <BubbleContent
      className="messageItem-content instagramComment"
      data-aid={`messageItem-instagram-comment-${item?.id}`}
      item={item}
    >
      <CommentText item={item} />
      <ModerationStatusIndicator item={item} />
    </BubbleContent>
  );
}

const CommentText = ({ item }) => {
  const { isViewTranslation } = useContext(ItemContext);
  const text = useConditionalTranslation(item, !isViewTranslation);

  const isEmpty = !text || text.trim() === '';
  const displayText = isEmpty
    ? `User responded using an unsupported image type. Please log into your organization's Instagram account to view their response.`
    : text;

  return (
    <CommentTextWrapper isEmpty={isEmpty}>
      <InlineAnnotatedContentContainer
        hasRedactedPaymentCard={item.content.hasRedactedPaymentCardNumber()}
        id={item.id}
        isRedacted={item.content.isRedacted}
        text={displayText}
      />
    </CommentTextWrapper>
  );
};

CommentText.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

export const ModerationStatusIndicator = ({ item }) => {
  const moderationStatus = item.content.getModerationStatus();

  if (
    !moderationStatus ||
    (moderationStatus !== ModerationStatus.HIDDEN && moderationStatus !== ModerationStatus.DELETED)
  ) {
    return null;
  }

  let message = 'This message was deleted from Instagram';
  if (moderationStatus === ModerationStatus.HIDDEN) {
    message = "This message was hidden. The person who posted can still see it, but it's hidden from everyone else.";
  }

  return (
    <ModerationStatusContainer data-aid={`moderation-status-${moderationStatus.toLowerCase()}`}>
      <ModerationStatusMessage>
        <StyledInfoIcon />
        {message}
      </ModerationStatusMessage>
    </ModerationStatusContainer>
  );
};

ModerationStatusIndicator.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

const InstagramCommentContainer = styled.div`
  margin-bottom: 8px;
`;

const ModerationStatusContainer = styled.div`
  margin-top: 8px;
`;

const ModerationStatusMessage = styled.div`
  color: ${p => p.theme.colors.gray600};
  font-size: var(--font-size-base);
  vertical-align: middle;
`;

const CommentTextWrapper = styled.div`
  ${p => p.isEmpty && `color: ${p.theme.colors.gray600};`}
`;

const PlayButtonOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  pointer-events: none;

  svg {
    width: 48px;
    height: 48px;
  }
`;

const PostContentContainer = styled.div`
  width: 100%;
  border-left: 4px solid ${p => p.theme.colors.gray300};
  padding-left: 12px;
  margin-bottom: 8px;
`;

const PostImage = styled.img`
  width: 100%;
  max-width: 280px;
  max-height: 200px;
  object-fit: cover;
  border-radius: 8px;
`;

const PostImageContainer = styled.div`
  margin-bottom: 8px;
`;

const PostText = styled.div`
  font-size: var(--font-size-base);
  color: ${p => p.theme.colors.gray700};
  line-height: 1.4;
  max-width: 280px;
  word-wrap: break-word;
`;

const PostVideoContainer = styled.div`
  position: relative;
  margin-bottom: 8px;
  max-width: 280px;
`;

const StyledInfoIcon = styled(InformationIcon)`
  display: inline-flex;
  fill: ${p => p.theme.colors.gray600};
  height: 15px;
  margin-right: 5px;
  vertical-align: middle;
`;

const VideoPlayer = styled(ReactPlayer)`
  border-radius: 8px;
  overflow: hidden;
`;
